import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useI18n } from '../../i18n/index.jsx';
import { useCategories } from '../../data.js';
import { pickLang } from '../../content.jsx';
import { deptPath, subPath, typePath } from '../../catalogue.js';

// Left panel of the department / product type pages: the whole catalogue
// (sectors › departments › sub-categories › product types, as published — hidden items
// never appear) as an accordion tree with thumbnails and product counts.
// The current page's branch is open and highlighted. A click on a name
// opens its page; a click on the arrow only opens / closes the branch.
// Desktop: sticky, can be folded to a thin bar (remembered). Phone: a
// drawer opened from "☰ Catégories" (CatalogueNavButton).

const STORE = 'pme_cat_nav_folded';
const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const readFolded = () => { try { return localStorage.getItem(STORE) === '1'; } catch { return false; } };

export function useNavFolded() {
  const [folded, setFolded] = useState(readFolded);
  const set = (v) => { setFolded(v); try { localStorage.setItem(STORE, v ? '1' : '0'); } catch { /* private mode */ } };
  return [folded, set];
}

function Thumb({ src, name, hue }) {
  const [broken, setBroken] = useState(false);
  return (
    <span className="cnav-thumb" style={{ '--h': hue }} aria-hidden="true">
      {src && !broken ? <img src={src} alt="" loading="lazy" decoding="async" onError={() => setBroken(true)} /> : String(name || '?').slice(0, 1)}
    </span>
  );
}

export function useNarrow() {
  const q = '(max-width: 900px)';
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return narrow;
}

export default function CatalogueNav({ sectors, current, folded: foldedPref, setFolded, drawerOpen, setDrawerOpen }) {
  const { t, lang } = useI18n();
  // Folding is for wide screens; phones and tablets use the drawer.
  const narrow = useNarrow();
  const folded = foldedPref && !narrow;
  const location = useLocation();
  const categories = useCategories();
  const live = useMemo(() => new Map(categories.map((c) => [c.slug, c.liveCount || 0])), [categories]);
  const [q, setQ] = useState('');
  const branch = () => [current.sector, current.dept && `${current.sector}/${current.dept}`, current.sub && `${current.sector}/${current.dept}/${current.sub}`].filter(Boolean);
  const [open, setOpen] = useState(() => new Set(branch()));
  const panelRef = useRef(null);

  // Moving to another page opens its branch (and closes the phone drawer).
  useEffect(() => {
    setOpen((o) => new Set([...o, ...branch()]));
    setDrawerOpen(false);
  }, [current.sector, current.dept, current.sub, current.type, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!drawerOpen) return undefined;
    panelRef.current?.querySelector('.cnav-search input')?.focus();
    const esc = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', esc);
    document.body.classList.add('cnav-drawer-open');
    return () => { document.removeEventListener('keydown', esc); document.body.classList.remove('cnav-drawer-open'); };
  }, [drawerOpen, setDrawerOpen]);

  // Tree with counts (approved products in the item's category and below).
  const tree = useMemo(() => sectors.map((s) => {
    const count = (slug) => (slug ? live.get(slug) || 0 : 0);
    const depts = (s.departments || []).map((d) => {
      const subs = (d.subs || []).map((sb) => {
        const kids = (sb.types || []).filter((ty) => !ty.hidden).map((ty) => ({
          key: `${s.key}/${d.id}/${sb.id}/${ty.id}`, name: pickLang(ty.name, lang), photo: ty.photos?.[0], to: typePath(s, d, sb, ty, lang), count: count(ty.categorySlug), kids: [],
        }));
        return { key: `${s.key}/${d.id}/${sb.id}`, name: pickLang(sb.name, lang), photo: sb.photo || kids.find((x) => x.photo)?.photo, to: subPath(s, d, sb, lang), kids,
          count: sb.categorySlug ? count(sb.categorySlug) : kids.reduce((n, x) => n + x.count, 0) };
      });
      return { key: `${s.key}/${d.id}`, name: pickLang(d.name, lang), photo: d.photo, to: deptPath(s, d, lang), kids: subs,
        count: d.categorySlug ? count(d.categorySlug) : subs.reduce((n, x) => n + x.count, 0) };
    });
    return { key: s.key, name: pickLang(s.name, lang), photo: s.photo || `/sectors/${s.key}.webp`, kids: depts,
      to: `/${lang === 'en' ? 'sectors' : 'secteurs'}/${s.key}`, count: depts.reduce((n, x) => n + x.count, 0) };
  }), [sectors, lang, live]);

  // Search: items whose name matches, with what's above them; all open.
  const words = norm(q).split(/\s+/).filter(Boolean);
  const hit = (name) => words.every((w) => norm(name).includes(w));
  const filter = (list, above) => list.map((x) => ({ ...x, kids: filter(x.kids, above || hit(x.name)) }))
    .filter((x) => above || hit(x.name) || x.kids.length);
  const filtered = !words.length ? tree : filter(tree, false);
  const isOpen = (k) => (words.length ? true : open.has(k));
  const toggle = (k) => setOpen((o) => { const n = new Set(o); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const keys = branch();
  const here = current.type ? `${keys[2]}/${current.type}` : keys[keys.length - 1];
  const row = (item, level, kids) => {
    const isHere = item.key === here;
    const isBranch = !isHere && keys.includes(item.key);
    const hasKids = kids && kids.length > 0;
    return (
      <div className={`cnav-row cnav-l${level}${isHere ? ' is-current' : ''}${isBranch ? ' is-branch' : ''}`}>
        {hasKids ? (
          <button type="button" className="cnav-toggle" onClick={() => toggle(item.key)} aria-expanded={isOpen(item.key)}
            aria-label={`${isOpen(item.key) ? t('catalogue.navCollapse') : t('catalogue.navExpand')} — ${item.name}`}>
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        ) : <span className="cnav-toggle-space" />}
        <Link to={item.to} className="cnav-link" aria-current={isHere ? 'page' : undefined}>
          <Thumb src={item.photo} name={item.name} hue={(item.key.length * 47) % 360} />
          <span className="cnav-name">{item.name}</span>
          {item.count > 0 && <span className="cnav-count" aria-label={t('catalogue.navCount', { n: item.count })}>{item.count}</span>}
        </Link>
      </div>
    );
  };

  const branchList = (list, level) => list.map((x) => (
    <li key={x.key}>
      {row(x, level, x.kids)}
      {isOpen(x.key) && x.kids.length > 0 && <ul>{branchList(x.kids, level + 1)}</ul>}
    </li>
  ));

  if (folded) {
    return (
      <aside className="cnav is-folded" aria-label={t('catalogue.navTitle')}>
        <button type="button" className="cnav-fold" onClick={() => setFolded(false)} aria-expanded="false" aria-label={t('catalogue.navOpen')} title={t('catalogue.navOpen')}>»</button>
        <span className="cnav-folded-label" aria-hidden="true">{t('catalogue.navTitle')}</span>
      </aside>
    );
  }
  return (
    <>
      {drawerOpen && <div className="cnav-scrim" onClick={() => setDrawerOpen(false)} aria-hidden="true" />}
      <aside ref={panelRef} id="catalogue-nav" className={`cnav${drawerOpen ? ' is-drawer-open' : ''}`} aria-label={t('catalogue.navTitle')}
        {...(narrow && !drawerOpen ? { inert: '' } : {})}>
        <div className="cnav-head">
          <strong>{t('catalogue.navTitle')}</strong>
          <button type="button" className="cnav-fold cnav-fold-desk" onClick={() => setFolded(true)} aria-expanded="true" aria-label={t('catalogue.navFold')} title={t('catalogue.navFold')}>«</button>
          <button type="button" className="cnav-fold cnav-close" onClick={() => setDrawerOpen(false)} aria-label={t('common.close')}>✕</button>
        </div>
        <label className="cnav-search">
          <span className="sr-only">{t('catalogue.navSearch')}</span>
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM21 21l-4.3-4.3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('catalogue.navSearch')} />
        </label>
        {!filtered.length && <p className="cnav-empty muted small">{t('catalogue.navNoMatch')}</p>}
        <ul className="cnav-tree">{branchList(filtered, 0)}</ul>
      </aside>
    </>
  );
}

// Phone / tablet: opens the panel as a drawer.
export function CatalogueNavButton({ onClick, open }) {
  const { t } = useI18n();
  return <button type="button" className="btn btn-ghost btn-sm cnav-open-btn" onClick={onClick} aria-expanded={open} aria-controls="catalogue-nav">☰ {t('catalogue.navTitle')}</button>;
}
